import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Loader2 } from 'lucide-react';
import PageIntro from '../../components/PageIntro/PageIntro';
import { Button } from '../../components/ui/button';
import DraftCard from '../../components/CbtQuestionRuns/DraftCard';
import { RunStatusPill } from '../../components/CbtQuestionRuns/Pills';
import {
  RUN_STATUS,
  describeError,
  inputClass,
  itemTypeLabel,
  levelLabel,
  runItemName,
} from '../../components/CbtQuestionRuns/cbtQuestionRuns.config';
import {
  useApproveCbtRunMutation,
  useCancelCbtRunMutation,
  useCheckCbtRunMutation,
  useGetCbtRunQuery,
  useRetryCbtRunMutation,
  useSendBackCbtRunMutation,
  useSubmitCbtRunMutation,
} from '../../reactWrapper/Redux/apiSlice';

/**
 * One run: its progress while the AI works, then the review.
 *
 *   ready / sent back   review the drafts, then Submit for approval
 *   submitted           an approver Approves or Sends back (drafts are frozen)
 *   approved            read only: what went into the exam bank
 */

// The statuses in which drafts can be changed (the API's rule)
const REVIEWABLE = ['ready', 'sent_back'];
// The statuses the API lets an admin cancel
const CANCELLABLE = ['queued', 'failed', 'ready', 'sent_back'];

export default function CbtQuestionRun() {
  const { id } = useParams();
  const [filter, setFilter] = useState('all'); // 'all' | 'look' | 'deleted'
  const [confirming, setConfirming] = useState(null); // null | 'submit' | 'approve' | 'back' | 'cancel'
  const [note, setNote] = useState('');

  // Refresh every 15 seconds while the cron is still writing or checking
  const [pollEvery, setPollEvery] = useState(15000);
  const { data: run, isLoading, isError, error } = useGetCbtRunQuery(id, {
    pollingInterval: pollEvery,
    refetchOnMountOrArgChange: true,
  });

  const status = run ? run.status : null;
  useEffect(() => {
    if (status && RUN_STATUS[status] && RUN_STATUS[status].working) {
      setPollEvery(15000);
    } else {
      setPollEvery(0); // 0 turns polling off
    }
  }, [status]);

  const [checkRun, checkState] = useCheckCbtRunMutation();
  const [submitRun, submitState] = useSubmitCbtRunMutation();
  const [sendBackRun, sendBackState] = useSendBackCbtRunMutation();
  const [approveRun, approveState] = useApproveCbtRunMutation();
  const [retryRun, retryState] = useRetryCbtRunMutation();
  const [cancelRun, cancelState] = useCancelCbtRunMutation();
  const busy =
    checkState.isLoading ||
    submitState.isLoading ||
    sendBackState.isLoading ||
    approveState.isLoading ||
    retryState.isLoading ||
    cancelState.isLoading;

  if (isLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading run…</div>;
  }
  if (isError) {
    return <div className="p-8 text-sm text-red-700">{describeError(error)}</div>;
  }

  const drafts = run.drafts || [];
  const kept = drafts.filter((draft) => draft.status === 'kept');
  const deleted = drafts.filter((draft) => draft.status === 'deleted');
  const needLook = kept.filter((draft) => draft.checker_result === 'disagreed' || draft.checker_result === 'flagged');
  const disagreed = kept.filter((draft) => draft.checker_result === 'disagreed');

  const isWorking = RUN_STATUS[run.status] && RUN_STATUS[run.status].working;
  const isReviewable = REVIEWABLE.includes(run.status);

  // The cards shown under the current filter
  let shown = drafts;
  if (filter === 'look') {
    shown = needLook;
  } else if (filter === 'deleted') {
    shown = deleted;
  }

  // Each draft keeps the same number whatever the filter
  const numberOf = {};
  drafts.forEach((draft, index) => {
    numberOf[draft.id] = index + 1;
  });

  // Run one API call, show the server's message, and close the confirm bar
  async function callApi(request, successMessage) {
    try {
      const response = await request.unwrap();
      toast.success(response.message || successMessage);
      setConfirming(null);
      setNote('');
    } catch (requestError) {
      toast.error(describeError(requestError));
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-28">
      <Link to="/cbt/questions" className="inline-flex w-max items-center gap-1 text-sm font-semibold text-brand-navy-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> Generate questions
      </Link>

      <PageIntro
        title={`${itemTypeLabel(run.item_type)}: ${runItemName(run)}`}
        description={`${levelLabel(run.level_code)} · ${run.question_count} questions requested · run #${run.id}`}
        actions={<RunStatusPill status={run.status} />}
      />

      {/* ---------- Messages ---------- */}
      {run.error_message && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>{run.error_message}</span>
          {/* A failed check leaves the run 'written'; the cron retries, or check now */}
          {run.status === 'written' && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => callApi(checkRun(run.id), 'Checked.')}>
              {checkState.isLoading ? 'Checking…' : 'Check again now'}
            </Button>
          )}
          {run.status === 'failed' && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => callApi(retryRun(run.id), 'Queued again.')}>
              Try again
            </Button>
          )}
        </div>
      )}

      {run.review_note && (run.status === 'sent_back' || run.status === 'submitted') && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <b>Approver's note:</b> {run.review_note}
        </div>
      )}

      {/* ---------- While the AI works ---------- */}
      {isWorking && (
        <section className="rounded-xl border bg-card p-5">
          <div className="flex items-center gap-2 font-semibold text-brand-navy">
            <Loader2 className="h-4 w-4 animate-spin" />
            {RUN_STATUS[run.status].label}
          </div>
          {run.status === "writing" && run.batch_id ? (
            // Sent as a Message Batch: Anthropic answers it later, at half price
            <p className="mt-1 text-sm text-muted-foreground">
              Sent to Anthropic's batch queue at {run.updated_at}. Batches are written at half price and are usually
              answered within an hour (24 hours at most); Gemini then checks them. You can leave this page; it refreshes
              on its own.
            </p>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              Claude writes the questions, then Gemini checks them, in the background every few minutes. You can leave
              this page; it refreshes on its own.
            </p>
          )}
        </section>
      )}

      {/* ---------- The drafts ---------- */}
      {drafts.length > 0 && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <Stat value={kept.length} label={run.status === 'approved' ? 'in the exam bank' : 'kept'} />
            <Stat value={needLook.length} label="need a look" tone="text-amber-700" />
            <Stat value={deleted.length} label="deleted" tone="text-red-700" />
          </div>

          <div className="inline-flex w-max overflow-hidden rounded-md border bg-card">
            <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label={`All (${drafts.length})`} />
            <FilterButton active={filter === 'look'} onClick={() => setFilter('look')} label={`Need a look (${needLook.length})`} />
            <FilterButton active={filter === 'deleted'} onClick={() => setFilter('deleted')} label={`Deleted (${deleted.length})`} />
          </div>

          <div className="flex flex-col gap-3">
            {shown.map((draft) => (
              <DraftCard key={draft.id} draft={draft} number={numberOf[draft.id]} editable={isReviewable} />
            ))}
            {shown.length === 0 && (
              <p className="rounded-xl border border-dashed bg-card p-6 text-center text-sm text-muted-foreground">
                No questions match this filter.
              </p>
            )}
          </div>
        </>
      )}

      {/* ---------- Actions bar (fixed at the bottom) ---------- */}
      {(isReviewable || run.status === 'submitted' || CANCELLABLE.includes(run.status)) && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-card px-4 py-3 shadow-[0_-6px_20px_rgba(10,31,68,.06)]">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
            {confirming === 'back' && (
              <>
                <input
                  className={`${inputClass} min-w-0 flex-1`}
                  placeholder="What should the reviewer change? (shown on the run)"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setConfirming(null)}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-brand-navy text-white hover:bg-brand-navy/90"
                    disabled={busy || note.trim() === ''}
                    onClick={() => callApi(sendBackRun({ id: run.id, note: note }), 'Sent back.')}
                  >
                    Send back
                  </Button>
                </div>
              </>
            )}

            {(confirming === 'submit' || confirming === 'approve' || confirming === 'cancel') && (
              <>
                <div className="min-w-0 text-sm">
                  <b className="text-brand-navy">{confirmTitle(confirming, kept.length)}</b>
                  <span className="block text-muted-foreground">{confirmText(confirming)}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setConfirming(null)}>
                    Back
                  </Button>
                  <Button
                    className={
                      confirming === 'cancel'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-brand-orange text-brand-navy hover:bg-brand-orange-600'
                    }
                    disabled={busy}
                    onClick={() => {
                      if (confirming === 'submit') {
                        callApi(submitRun(run.id), 'Submitted for approval.');
                      } else if (confirming === 'approve') {
                        callApi(approveRun(run.id), 'Approved.');
                      } else {
                        callApi(cancelRun(run.id), 'Run cancelled.');
                      }
                    }}
                  >
                    Yes, {confirming}
                  </Button>
                </div>
              </>
            )}

            {confirming === null && (
              <>
                <div className="text-sm">
                  <b className="text-brand-navy">{kept.length} questions kept</b>
                  {disagreed.length > 0 && isReviewable && (
                    <span className="block text-red-700">
                      {disagreed.length} still disagree with the checker: edit, delete or confirm them before submitting.
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {CANCELLABLE.includes(run.status) && (
                    <Button variant="outline" onClick={() => setConfirming('cancel')}>
                      Cancel run
                    </Button>
                  )}
                  {isReviewable && (
                    <Button
                      className="bg-brand-orange text-brand-navy hover:bg-brand-orange-600"
                      disabled={kept.length === 0}
                      onClick={() => setConfirming('submit')}
                    >
                      Submit for approval
                    </Button>
                  )}
                  {run.status === 'submitted' && (
                    <>
                      <Button variant="outline" onClick={() => setConfirming('back')}>
                        Send back
                      </Button>
                      <Button
                        className="bg-brand-orange text-brand-navy hover:bg-brand-orange-600"
                        onClick={() => setConfirming('approve')}
                      >
                        Approve run
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function confirmTitle(action, keptCount) {
  if (action === 'submit') {
    return `Submit ${keptCount} questions for approval?`;
  }
  if (action === 'approve') {
    return `Approve ${keptCount} questions?`;
  }
  return 'Cancel this run?';
}

function confirmText(action) {
  if (action === 'submit') {
    return 'The questions are frozen until an approver approves them or sends the run back.';
  }
  if (action === 'approve') {
    return 'They are copied into the exam bank straight away.';
  }
  return 'A cancelled run cannot be reopened. Its questions will never reach the exam.';
}

function Stat({ value, label, tone = 'text-brand-navy' }) {
  return (
    <span>
      <b className={`mr-1 font-display text-2xl ${tone}`}>{value}</b>
      <span className="text-sm text-muted-foreground">{label}</span>
    </span>
  );
}

function FilterButton({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-medium ${active ? 'bg-brand-navy text-white' : 'text-muted-foreground'}`}
    >
      {label}
    </button>
  );
}
